import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Collapse, Typography } from '@mui/material'
import React from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { useUser } from 'src/Hooks/useUser'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { NumberField, TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { UnitTypes } from 'src/Libs/types'

export const BatteryOperationRange: React.FC<{ storageIndex: number }> = ({ storageIndex }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { userData: user } = useUser()
  const canConfigureBatteryLowerDischargeThreshold = user?.level === 'EXT_SERVICE_PLUS' || user?.level === 'INSTALLER'

  const { control, setValue } = useInstallationForm()
  const { errors } = useFormState({
    control,
    name: `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange`,
  })

  const storage = useWatch({
    control,
    name: `storageDevices.${storageIndex}`,
  })
  const storageBatteryCount = useWatch({
    control,
    name: `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryNumber`,
  })
  const isStorageSingleBattery = useWatch({
    control,
    name: `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.isStorageSingleBattery`,
  })
  const isBatteryOperationRangeAuto = useWatch({
    control,
    name: `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.isAuto`,
  })
  const maximumChargingPower = useWatch({
    control,
    name: `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumChargingPower`,
  })
  const maximumDischargingPower = useWatch({
    control,
    name: `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumDischargingPower`,
  })

  const storageType = storage?.deviceType

  const showEmsValuesError =
    storageBatteryCount === 1 &&
    (errors?.storageSystem?.storageSpecificSettings?.[storageIndex]?.advancedSettings?.batteryOperationRange?.maximumChargingPower ||
      errors?.storageSystem?.storageSpecificSettings?.[storageIndex]?.advancedSettings?.batteryOperationRange?.maximumDischargingPower)
  const defaultMaxPower = isStorageSingleBattery || storageType === 'XEM - R1' ? 3000 : 4500

  const handleSwitchChange = (checked: string | number | boolean): void => {
    if (!checked) {
      setValue(
        `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumChargingPower`,
        maximumChargingPower ?? defaultMaxPower,
        {
          shouldDirty: true,
        }
      )
      setValue(
        `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumDischargingPower`,
        maximumDischargingPower ?? defaultMaxPower,
        {
          shouldDirty: true,
        }
      )
      setValue(
        `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.minimumConsumptionPowerToStartDischarge`,
        50,
        { shouldDirty: true }
      )
    }
  }

  return (
    <Box component="section">
      <SettingsBox
        title={t('installation.storageEditor.batteryOperatingRange.title')}
        description={t('installation.storageEditor.batteryOperatingRange.subtitle')}
        suffix={
          <TabButtonGroup
            name={`storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.isAuto`}
            options={[
              {
                label: t('installation.storageEditor.batteryOperatingRange.manual'),
                value: false,
              },
              {
                label: t('installation.storageEditor.batteryOperatingRange.auto'),
                value: true,
              },
            ]}
            disabled={!canEdit}
            onChange={handleSwitchChange}
          />
        }
      >
        <Collapse in={!isBatteryOperationRangeAuto}>
          <SettingsBox
            title={t('installation.storageEditor.maxChargePower')}
            suffix={
              <NumberField
                name={`storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumChargingPower`}
                label={t('installation.storageEditor.maxChargePower')}
                suffix={UnitTypes.watt}
                inputProps={{
                  min: 0,
                  max: defaultMaxPower,
                }}
                disabled={!canEdit}
              />
            }
            depth={1}
          />
          <SettingsBox
            title={t('installation.storageEditor.maxDischargePower')}
            suffix={
              <NumberField
                name={`storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumDischargingPower`}
                label={t('installation.storageEditor.maxDischargePower')}
                suffix={UnitTypes.watt}
                inputProps={{
                  min: 0,
                  max: defaultMaxPower,
                }}
                disabled={!canEdit}
              />
            }
            depth={1}
          />

          {canConfigureBatteryLowerDischargeThreshold && (
            <SettingsBox
              title={t('installation.storageEditor.lowerDischargeThreshold')}
              suffix={
                <NumberField
                  name={`storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.minimumConsumptionPowerToStartDischarge`}
                  label={t('installation.storageEditor.lowerDischargeThreshold')}
                  suffix={UnitTypes.watt}
                  inputProps={{
                    min: 0,
                  }}
                  disabled={!canEdit}
                />
              }
              depth={1}
            />
          )}
        </Collapse>
      </SettingsBox>

      {showEmsValuesError && (
        <Typography variant="subtitle2" component={'p'} pt={1}>
          {t('installation.storageEditor.emsValuesError')}
        </Typography>
      )}
    </Box>
  )
}
